import { defineStore } from 'pinia';
import { useApi } from '@/lib/api';
import { useGlobalStore } from '@/store/global-store-base';
import { useCurrencies } from '@/store/currencies.ts';
import { ClientError } from '@/lib/client-error';
import { useSeasons } from './seasons';

export const useCurrencyExchangeRates = defineStore('currency-exchange-rates', () => {
  const api = useApi();
  const globalStoreBase = useGlobalStore({
    load: api.currency_GetAllCurrencyExchangeRatesForBrand,
    dto: 'CurrencyExchangeRateDto',
    create: api.currency_AddCurrencyExchangeRate,
    update: api.currency_UpdateCurrencyExchangeRate,
    delete: api.currency_RemoveCurrencyExchangeRate,

    signalR: 'CurrencyExchangeRateUpdate',
  });

  const { loaded } = globalStoreBase;

  /**
   * The rate to use between two currencies (either direction), preferring a season-specific
   * rate over the brand default. Single source of truth for both hasRate and the conversions,
   * so they can never disagree about whether a conversion is possible.
   */
  function findRate(fromId: number, toId: number, seasonId?: number | null) {
    return loaded.value
      .filter(
        (c) =>
          ((c.fromId === fromId && c.toId === toId) || (c.fromId === toId && c.toId === fromId)) &&
          (c.seasonId === seasonId || c.seasonId === null)
      )
      .sort((a, b) => {
        // Priority 1: Season match over brand default
        const aSeasonMatch = seasonId !== undefined && a.seasonId === seasonId;
        const bSeasonMatch = seasonId !== undefined && b.seasonId === seasonId;
        if (aSeasonMatch !== bSeasonMatch) return aSeasonMatch ? -1 : 1;

        // Priority 2: Matching direction (from -> to) over inverse
        const aDirectMatch = a.fromId === fromId && a.toId === toId;
        const bDirectMatch = b.fromId === fromId && b.toId === toId;
        if (aDirectMatch !== bDirectMatch) return aDirectMatch ? -1 : 1;

        return 0;
      })[0];
  }

  // Whether a rate exists to convert between two currencies (either direction),
  // for the given season or the brand default. Lets callers offer "Convert" only
  // when it can actually succeed, rather than letting convertCurrency throw.
  function hasRate(fromId: number, toId: number, seasonId?: number | null): boolean {
    if (fromId === toId) return true;

    return findRate(fromId, toId, seasonId) !== undefined;
  }

  /**
   * Converts an amount, or returns undefined when no rate exists. For display paths, where a
   * missing rate is a value that cannot be computed rather than something to tell the user about.
   */
  function tryConvertCurrency(
    amount: number,
    fromId: number,
    toId: number,
    seasonId?: number | null
  ): number | undefined {
    if (!amount || fromId === toId) {
      return amount;
    }

    const rate = findRate(fromId, toId, seasonId);
    if (!rate) {
      return undefined;
    }

    const exchangeRate = rate.fromId === fromId ? rate.exchangeRate : 1 / rate.exchangeRate;

    return amount * exchangeRate;
  }

  /** Converts an amount, throwing when no rate exists. For user actions, where that is worth saying. */
  function convertCurrency(amount: number, fromId: number, toId: number, seasonId?: number | null): number {
    const converted = tryConvertCurrency(amount, fromId, toId, seasonId);
    if (converted === undefined) {
      throw new ClientError(missingRateMessage(fromId, toId, seasonId));
    }

    return converted;
  }

  /** Describes a missing rate, for the thrown error and for the cells that render blank instead. */
  function missingRateMessage(fromId: number, toId: number, seasonId?: number | null): string {
    const fromCurrency = useCurrencies().getById(fromId);
    const toCurrency = useCurrencies().getById(toId);
    const season = seasonId != null ? useSeasons().getById(seasonId) : undefined;

    return `No exchange rate found for ${fromCurrency?.code} (id: ${fromId}) to ${toCurrency?.code} (id: ${toId})${season ? ` for season ${season.name} (id: ${seasonId})` : ''}`;
  }

  return { ...globalStoreBase, convertCurrency, tryConvertCurrency, hasRate, missingRateMessage };
});
